import { type PluginListenerHandle } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { CapacitorDownloader } from "@capgo/capacitor-downloader";
// The engine is device-scoped background work: all of its server fetches use
// the device transport (real signed-in user, never the acting-as header), so
// an active impersonation can't misattribute downloads or 403-fail the queue
// for rounds outside the impersonated user's access.
import * as Sentry from "@client/telemetry/sentry";
import { buildDeviceAuthHeaders, trpcVanillaDevice } from "@client/trpc";
import { devDebugLog } from "@client/utils/devDebug";
import { captureEngineHydrateCompleted } from "@client/utils/downloadEngineAnalytics";
import { videoPosterUrl } from "@client/utils/videoPosterUrl";
import {
  downloadEngineStatusStore,
  downloadEngineUnavailableMessage,
} from "./downloadEngineStatus";
import {
  deleteFilesBatch,
  downloadBatch,
  stopBatch,
  type BatchDownloadResult,
} from "./downloadBatchBridge";
import { downloadProgressStore } from "./downloadProgress";
import { MediaCacheQueue } from "./mediaCacheQueue";
import { orderByWatchPriority } from "./downloadPriority";
import {
  downloadSizeMatches,
  expectedDownloadSizeBytes,
  isDownloadGenerationStale,
  resolveLocalFileMimeType,
  stampsFromDownloadUrlResponse,
  type LocalFileStat,
} from "./downloadVerification";
import { isMissingFileError } from "./fileErrors";
import {
  verifyDownloadedMedia,
  type MediaVerification,
} from "./mediaVerification";
import {
  MobileVideoStore,
  type MobileVideo,
  type MobileVideoDownload,
} from "./mobileVideoStore";
import { shouldCachePoster } from "./posterSrc";

async function safeStatFile(path: string): Promise<LocalFileStat> {
  try {
    const result = await Filesystem.stat({
      directory: Directory.Documents,
      path,
    });
    return { exists: true, uri: result.uri, size: result.size };
  } catch {
    return { exists: false };
  }
}

// Hand the main thread back between queue items.
//
// Awaiting a bridge call is NOT enough on its own: each one resolves into a
// microtask that runs straight into the next item's bridge call, so a 184-video
// handoff is a near-continuous stream of native work. WebKit needs a clear
// macrotask turn to deliver a tap's `click`, and without one the queue drawer
// ignored taps for the length of the handoff (device log: three taps delivered
// touchstart/touchend with no click at all).
//
// `setTimeout(0)` rather than a microtask or `requestAnimationFrame`: it yields
// to the task queue where input dispatch is waiting, and unlike rAF it keeps
// running when the WebView is backgrounded — the handoff must finish even with
// the app suspended, which is the whole reason the queue is pushed to the
// native session up front.
function yieldToMainThread(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

// Reports whether the file is actually gone. `safeDeleteFile` swallows, which
// is right where a leftover is harmless, but the wipe has to know: it clears
// the residue bookkeeping that decides whether a later download bothers to
// clean up first.
async function tryDeleteFile(path: string): Promise<boolean> {
  try {
    await Filesystem.deleteFile({ directory: Directory.Documents, path });

    return true;
  } catch (error) {
    // Already gone is success — the caller's intent is "make sure this is
    // not there", and a wipe re-run after a crash must not report the files
    // the first run removed.
    return isMissingFileError(error);
  }
}

async function safeDeleteFile(path: string): Promise<void> {
  try {
    await Filesystem.deleteFile({ directory: Directory.Documents, path });
  } catch {
    // File may already be gone — ignore.
  }
}

async function blobToBase64(blob: Blob): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

  // Filesystem.writeFile wants bare base64, not a data: URL.
  return dataUrl.slice(dataUrl.indexOf(",") + 1);
}

// Coalesce `downloadProgress` plugin events: the plugin fires very frequently
// and writing every event into the store would re-render the queue UI hard
// enough to flicker the progress bars.
const progressFlushIntervalMs = 100;

// Exponential backoff caps for auto-retry while the global download toggle
// is on. We never give up — eventually a download succeeds or the user wipes.
const retryBaseMs = 2_000;
const retryMaxMs = 30_000;

// How many videos one batched signed-URL request covers (the route caps at
// 200). The whole queue is handed to the native downloader in as few
// requests as possible: per-item URL fetches needed a JS re-tick between
// waves, and the WebView can't run one once the phone locks — anything not
// yet enqueued into the iOS background URLSession stalled until the next
// foreground. Actual transfer concurrency is capped NATIVELY at 5 by the
// patched plugin (see patches/README.md): tasks beyond the cap wait
// suspended inside the plugin and are promoted from its terminal delegate
// callback, which runs during background wakes — so the capped drain still
// works while the app is backgrounded or the phone is locked, which a
// JS-side throttle could never do.
const signedUrlBatchSize = 100;

// Shown on a download that failed because the device is out of storage. There
// is no space budget or eviction, so retrying against a full disk would just
// spin — the item is left failed (no retry timer) until space is freed and a
// later catalog sync / relaunch re-queues it.
const insufficientStorageMessage =
  "Couldn't download — this device may be low on storage.";

// Best-effort recognition of an out-of-space failure from the downloader /
// Filesystem error text (iOS NSFileWriteOutOfSpaceError surfaces as "not
// enough space on the disk"). Matching is intentionally broad; a false
// negative just falls back to the normal retry path.
function isInsufficientStorageError(error: string): boolean {
  return /no space left|not enough (free )?space|out of (disk )?space|insufficient storage|disk (is )?full|quota exceeded|NSFileWriteOutOfSpace/i.test(
    error,
  );
}

type RetryBlockedReason = NonNullable<
  MobileVideoDownload["retryBlockedReason"]
>;

function unplayableMessage(reason: string) {
  return `Downloaded file is not playable: ${reason}`;
}

// A file AVFoundation refused is only worth another download once the server
// would serve a different one: a newer recording (generation), or a different
// served file of the same recording — a WebM original keeps its generation
// when its 720p rendition lands, but the served size changes. "Newer", not
// "different", for the generation: a download's stamp can run ahead of a
// stale catalog row, and that difference would re-download the same
// unplayable file on every sync. A served size of 0 means the server doesn't
// know it yet, which says nothing about the file.
function isUnplayableFileReplaced(
  download: MobileVideoDownload,
  video: MobileVideo,
) {
  if (download.retryBlockedReason !== "unplayable") {
    return false;
  }

  const servedSizeBytes = video.downloadSizeBytes ?? 0;
  // Only a stamped size can differ from the served one — a download the
  // server never sized (null stamp) would otherwise read as "different" on
  // every sync and re-download the same unplayable file forever.
  const stampedSizeBytes = download.expectedSizeBytes;

  return (
    isDownloadGenerationStale({
      fileUploadedAt: video.fileUploadedAt,
      download,
    }) ||
    (servedSizeBytes > 0 &&
      typeof stampedSizeBytes === "number" &&
      servedSizeBytes !== stampedSizeBytes)
  );
}

// How long startup waits for the persisted store to finish loading before it
// carries on without it. Only the media rejoin is async (per-video native
// files since SP-2623); the download records themselves land in hydrate's
// synchronous first phase, so exceeding this budget costs cached transcripts
// and chapters — which the backfills refetch — and never download state.
// Bounded because an unbounded wait here is what kept the listeners below
// from ever being attached (see `start`).
const defaultHydrateBudgetMs = 15_000;

// How long one offline-caching request (a transcript, a chapter set, a poster)
// may take before it is abandoned.
//
// Bounded for the same reason as the signed-URL request below, but with a
// sharper consequence now that these run through a bounded queue: a request
// that never settles — the half-open connection a phone gets walking between
// networks — holds its slot forever, and once every slot of a kind is held,
// offline caching stops for the whole session. Later videos stay queued, and
// the reconnect sweep cannot rescue them because their ids are still claimed.
// A timeout turns that into one abandoned video the next sweep picks up.
const mediaCacheRequestTimeoutMs = 30_000;

// How long one round's batched signed-URL request may take before the handoff
// treats it as failed. Without a bound, a request that never settles (a
// half-open connection on a flaky network — no error, no response) strands
// every video in the chunk: they stay "running" with no native task, and the
// pick keeps skipping them because the engine still believes their handoff is
// in flight. A timeout instead routes them through the normal retry path.
const signedUrlRequestTimeoutMs = 60_000;

// Startup problems are reported rather than swallowed. This whole class of
// bug is only expensive because it was silent: the queue looked ordinary
// while the engine could not run it, so nothing pointed at startup.
function reportDownloadEngineIssue(stage: string, error: unknown): void {
  console.warn("[download-engine] startup issue", { stage, error });
  Sentry.captureException(error, { tags: { downloadEngineStage: stage } });
}

// How long a navigation suspends download transfer. Five concurrent
// URLSession tasks pulling multi-GB video files saturate the WiFi link, and
// the navigation's own API fetches starve behind them — on the iPad this
// reads as "the tap registered but the page didn't change" (measured on
// device: 3.5-26s per rail switch, some never landing, vs instant with
// downloads idle). Suspending the transfers for a bounded window whenever
// the user navigates gives the navigation the whole pipe; repeated
// navigations extend the window rather than stacking holds.
const navigationHoldMs = 10_000;

// One video's entry in the batched signed-URL response. `signedDownloadUrl` is
// optional only so a partial/missing entry can flow through the same shape —
// callers treat its absence as "not available for download".
type DownloadUrlResponse = {
  signedDownloadUrl?: string;
  fileUploadedAt?: string | null;
  sizeBytes?: number | null;
  contentType?: string | null;
};

export class DownloadEngine {
  private readonly store: MobileVideoStore;
  private readonly hydrateBudgetMs: number;
  private listeners: PluginListenerHandle[] = [];
  // Set only once the native download listeners are attached — see `start`.
  // This is the flag `tick()` gates on, so it is precisely the promise "any
  // handoff this engine makes, it can hear the outcome of".
  private started = false;
  // Memoizes an in-flight/completed startup so concurrent mounts share one
  // run. `started` can no longer serve as that guard: it is now set at the
  // END of startup, so two callers would otherwise both start.
  private startPromise: Promise<void> | null = null;
  private isOnline = typeof navigator === "undefined" ? true : navigator.onLine;
  // Per-round wipe epochs. `wipeRound` bumps the epoch for the round it
  // clears so any in-flight handoff for THAT round detects the wipe
  // mid-await and abandons — while concurrent downloads of OTHER rounds keep
  // their (unchanged) epoch and proceed. A single global token would strand
  // an unrelated round's in-flight item in "running" forever.
  private readonly wipeEpochByRound = new Map<string, number>();
  // Wipes run one at a time: each holds the global `wiping` flag and every
  // affected file lock, so a second round's wipe waits here for the first
  // instead of being dropped.
  private wipeQueue: Promise<void> = Promise.resolve();
  // Offline caching of transcripts, chapters and posters — bounded and
  // de-duplicated per video (see MediaCacheQueue for the measurements that
  // forced both). One queue per kind, so a thumbnail's poster never waits
  // behind a round's worth of transcripts and vice versa.
  private readonly transcriptCacheQueue = new MediaCacheQueue();
  private readonly chapterCacheQueue = new MediaCacheQueue();
  private readonly posterCacheQueue = new MediaCacheQueue();
  // Video ids currently inside a handoff batch (signed-URL fetch → native
  // task kickoffs). The pick in `tick()` excludes them, so an id re-queued
  // mid-handoff can't be handed off twice.
  private readonly handoffInFlight = new Set<string>();
  // Ids that may have a native task or a file on disk, so a kickoff has to
  // cancel and delete before starting. Everything else skips both.
  //
  // The kickoff used to run stop() + deleteFile() unconditionally, which on a
  // fresh install is two guaranteed-to-fail bridge round trips per video —
  // measured on the funder's 184-video round as ~35 seconds of back-to-back
  // native calls where every single one returned "Task not found" /
  // "does not exist". That contention is what starved the queue drawer's
  // dismissal taps.
  //
  // Filled from the store at launch (see markResidueFromStore) and from every
  // download we hand off this run. A wrong "no residue" guess is not
  // dangerous, only slow: the plugin's move refuses to overwrite, so the
  // kickoff fails into the ordinary retry path, and by then the id IS marked —
  // so the retry does the cleanup and recovers. `residueRecoveryTest` in the
  // engine suite pins that.
  private readonly mayHaveNativeResidue = new Set<string>();
  private readonly pendingProgress = new Map<string, number>();
  // Navigation hold: ids of tasks THIS engine suspended (never ones paused
  // any other way), plus the window bookkeeping. Store status stays
  // "running" during a hold — the suspension is transport-level and the
  // tasks resume from the same byte offsets.
  private readonly navHeldTaskIds = new Set<string>();
  private navHoldTimer: ReturnType<typeof setTimeout> | null = null;
  private navHoldUntil = 0;
  private lastProgressFlushAt = 0;
  private progressFlushTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly retryTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();
  // Per-id attempt epochs, bumped by any code path that invalidates the bytes
  // at an id's destination (a new attempt's pre-download delete, reconcile's
  // impostor deletes, the play-time heal). A plugin event from a superseded
  // task snapshots the epoch on arrival; a mismatch after its awaits means
  // the file it describes is gone and the event must drop — recording
  // anything would bless deleted bytes as "completed" or park the live new
  // attempt in "failed".
  private readonly attemptEpochById = new Map<string, number>();
  // Per-id promise chain serializing every section that stats/verifies/
  // deletes an id's destination file. Without it a completion event's stat
  // can run concurrently with a superseding delete and bless bytes that are
  // already gone — a "completed" record with no file, which the next launch
  // reconcile turns into a full remove (playlist, bookmarks, watch progress).
  private readonly fileLockById = new Map<string, Promise<unknown>>();

  private readonly handleOnline = (): void => {
    this.handleOnlineChange(true);
  };

  private readonly handleOffline = (): void => {
    this.handleOnlineChange(false);
  };

  // Test seam only (`hydrateBudgetMs`); production uses the module default.
  constructor(
    store: MobileVideoStore,
    options: { hydrateBudgetMs?: number } = {},
  ) {
    this.store = store;
    this.hydrateBudgetMs = options.hydrateBudgetMs ?? defaultHydrateBudgetMs;
  }

  /**
   * Bring the engine up. Two things here are load-bearing, and both exist
   * because of the same production failure (the funder's iPad, Aug 2026):
   *
   * - **Nothing is handed to iOS before the listeners are attached.**
   *   `started` is what allows `tick()` to hand a video to the background
   *   URLSession, and `tick()` is reachable WITHOUT this method — the
   *   "Download all" toggle and every catalog sync call `syncWithCatalog`,
   *   which ticks directly. Setting `started` first (as this used to) meant a
   *   startup that stalled before `attachListeners` still handed the whole
   *   round to iOS while no listener existed to receive the progress,
   *   completion and failure events it sent back. iOS downloaded the round
   *   perfectly; the app heard none of it. Every video sat under "Queued"
   *   forever, nothing ever errored — failures ride the same listener — and
   *   neither a relaunch nor wiping and re-enabling the round recovered it,
   *   because both re-ran the identical sequence.
   * - **The store load is bounded.** It is the step that stalled: since
   *   SP-2623 it does per-video native file IO, which is heaviest on exactly
   *   the device most likely to be in iOS Lockdown Mode (no WebView
   *   IndexedDB, so all persistence is mirrored to native files). Loading the
   *   store is worth waiting for, but not at the cost of never running.
   *
   * Idempotent and safe to call concurrently: callers share one promise.
   */
  start(): Promise<void> {
    this.startPromise ??= this.startOnce();

    return this.startPromise;
  }

  private async startOnce(): Promise<void> {
    // Awaited (within the budget): hydrate's media rejoin is async
    // (file-backed side records), and the backfills below must not read
    // not-yet-rejoined nulls as never-fetched — that would refetch every
    // transcript on every launch.
    await this.hydrateWithinBudget();
    this.isOnline = typeof navigator === "undefined" ? true : navigator.onLine;

    try {
      await this.attachListeners();
    } catch (error) {
      // An engine with no listeners can only ever be deaf, so it must not
      // start: handing a video to iOS now would download bytes this app can
      // never learn about. Say so instead of showing a queue that will never
      // move, and clear the memo so a later mount can retry.
      this.startPromise = null;
      downloadEngineStatusStore.set({
        state: "unavailable",
        message: downloadEngineUnavailableMessage,
      });
      reportDownloadEngineIssue("attachListeners", error);

      return;
    }

    // Only now may anything reach the native downloader.
    this.started = true;
    downloadEngineStatusStore.set({ state: "ready" });

    if (typeof window !== "undefined") {
      window.addEventListener("online", this.handleOnline);
      window.addEventListener("offline", this.handleOffline);
    }

    // Before reconcile, and before any tick can hand something off: this is
    // the launch's one look at what the previous run may have left on the
    // device. Everything not listed here skips the kickoff's cancel + delete.
    this.markResidueFromStore();

    const legacyUnverified = await this.reconcile();
    this.runBackfills("startup", {
      transcripts: true,
      chapters: true,
      posters: true,
    });
    this.tick();
    await this.verifyLegacyDownloads(legacyUnverified);
  }

  // Wait for the persisted store, but never indefinitely — see `start`. A
  // timeout does NOT cancel the load: it keeps running and commits under the
  // store's own supersede rules, which already tolerate mutation landing
  // during hydrate. Failures are equally non-fatal; the download records are
  // in memory either way (hydrate's first phase is synchronous), so the worst
  // case is refetching transcripts the backfills already know how to refetch.
  private async hydrateWithinBudget(): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();

    try {
      const hydration = this.store.hydrate();

      // The TRUE load duration, reported even when the budget below stops
      // waiting — the load keeps running, and "is the budget bigger than
      // real devices need?" is only answerable from completed-load times.
      // Detached: a load that never settles reports nothing, and that
      // absence (launches with no hydrate event) is itself the signal.
      // Failures report nothing here either — the catch below already tells
      // Sentry.
      void hydration
        .then(() => {
          const elapsedMs = Date.now() - startedAt;
          captureEngineHydrateCompleted({
            hydrate_ms: elapsedMs,
            item_count: Object.keys(this.store.getState().items).length,
            over_budget: elapsedMs > this.hydrateBudgetMs,
          });
        })
        .catch(() => {});
      // Overrunning the budget is REPORTED, not just survived. A silent
      // recovery would leave the next person debugging this with the same
      // nothing we had: the app running fine on the fallback, and no signal
      // that the step it fell back from is the one still broken.
      const outcome = await Promise.race([
        hydration.then(() => "loaded" as const),
        new Promise<"timed-out">((resolve) => {
          timer = setTimeout(() => resolve("timed-out"), this.hydrateBudgetMs);
        }),
      ]);

      if (outcome === "timed-out") {
        reportDownloadEngineIssue(
          "hydrate",
          new Error(
            `Store load exceeded ${this.hydrateBudgetMs}ms; starting downloads without it`,
          ),
        );
      }
    } catch (error) {
      reportDownloadEngineIssue("hydrate", error);
    } finally {
      clearTimeout(timer);
    }
  }

  async stop(): Promise<void> {
    await this.removeListenerHandles(this.listeners);

    this.listeners = [];

    if (typeof window !== "undefined") {
      window.removeEventListener("online", this.handleOnline);
      window.removeEventListener("offline", this.handleOffline);
    }

    if (this.progressFlushTimer) {
      clearTimeout(this.progressFlushTimer);
      this.progressFlushTimer = null;
    }

    this.pendingProgress.clear();
    this.lastProgressFlushAt = 0;
    this.clearAllRetryTimers();

    // A task suspended by a navigation hold must not stay suspended past
    // the engine's lifetime — release (and resume) before tearing down.
    this.releaseNavigationHold();

    this.started = false;
    // Drop the memo with the listeners it stands for, so a later start()
    // re-attaches them rather than resolving instantly into a deaf engine.
    this.startPromise = null;
    downloadEngineStatusStore.set({ state: "idle" });
  }

  // Toggle "download all videos" for one round. On enable, every video in
  // `currentVideos` that isn't already completed gets enqueued. On disable,
  // that round's downloads are wiped from disk and the store, queued behind
  // any wipe already running. The UI gates re-enable on `state.wiping`; we
  // guard here as well so a stray enable mid-wipe is a no-op rather than a
  // race.
  async setDownloadAllEnabled(
    enabled: boolean,
    roundId: string,
    currentVideos: MobileVideo[],
  ): Promise<void> {
    if (!enabled) {
      this.store.setRoundDownloadEnabled(roundId, false);
      await this.queueWipe(roundId);

      return;
    }

    if (this.store.getState().wiping) {
      return;
    }

    this.store.setRoundDownloadEnabled(roundId, true);
    this.syncWithCatalog(currentVideos);
  }

  // The stored chain never rejects (a failed wipe must not block the next),
  // but the caller still sees its own wipe's rejection.
  private queueWipe(roundId: string): Promise<void> {
    const wipe = this.wipeQueue.then(() => this.wipeRound(roundId));

    this.wipeQueue = wipe.catch(() => {});

    return wipe;
  }

  // Auto-enqueue round videos that aren't yet downloaded while their round's
  // toggle is on. Safe to call repeatedly; `store.enqueue` is idempotent.
  // Download ORDER is not decided here — the store's `order` is already
  // populated in catalog order by syncCatalogMetadata, and enqueue only
  // appends new ids, so ordering happens at pick time (queuedByWatchPriority)
  // instead.
  syncWithCatalog(videos: MobileVideo[]): void {
    // Collect first, commit once. Enabling "Download all" on a fresh install
    // enqueues the round's entire catalog (184 videos on the funder's round),
    // and a commit per video re-serialized the whole blob to localStorage and
    // re-rendered every subscriber that many times — synchronously, inside the
    // tap that flipped the toggle.
    const toEnqueue: MobileVideo[] = [];

    for (const video of videos) {
      if (!this.store.isRoundDownloadEnabled(video.roundId)) {
        continue;
      }

      if (!video.isAvailable) {
        continue;
      }

      const existing = this.store.getState().items[video.id]?.download;

      if (existing?.status === "completed") {
        continue;
      }

      // Don't re-enqueue a "failed" item that is either mid-backoff (a live
      // retry timer would be reset, skipping the exponential delay) or blocked
      // (re-enqueuing would re-download in the loop the block exists to stop)
      // — unless the block was for a file the catalog has since replaced.
      if (
        existing?.status === "failed" &&
        (this.retryTimers.has(video.id) ||
          (existing.retryBlocked === true &&
            !isUnplayableFileReplaced(existing, video)))
      ) {
        continue;
      }

      toEnqueue.push(video);
    }

    this.store.enqueueMany(toEnqueue);
    this.tick();

    // Catalog sync is when `activePosterId` lands on already-downloaded items
    // (engine start can run before the first getVideos sync, and upgraded
    // devices hydrate with a null id). Re-run the poster backfill here so every
    // completed download caches its poster for offline — not only the rows a
    // thumbnail happens to mount.
    this.runBackfills("catalog-sync", {
      transcripts: false,
      chapters: false,
      posters: true,
    });
  }

  // Every queued download in the order it should reach the native session.
  // Priority is decided HERE, at pick time — not by enqueue order — because
  // the store's `order` is already populated in catalog order (by
  // syncCatalogMetadata, before the funder even accepts), so sorting at
  // enqueue would be a no-op. Ordering at pick time also means a
  // newly-discovered high-priority video joins the next handoff ahead of
  // lower-priority not-yet-started ones, without restarting anything already
  // running.
  private queuedByWatchPriority(
    excludeIds: ReadonlySet<string>,
  ): MobileVideo[] {
    // excludeIds are the ids still mid-handoff: an item re-queued while its
    // handoff is awaiting the signed-URL batch must wait for that handoff to
    // settle, or two concurrent handoffs would stack two native tasks on one
    // id (see tick()).
    const queued = this.store
      .itemsInOrder()
      .filter(
        (item) =>
          item.download?.status === "queued" && !excludeIds.has(item.id),
      );

    if (queued.length === 0) {
      return [];
    }

    // orderByWatchPriority (a pure, CI-tested helper) reads the current store
    // items' watch progress + the playlist order; the handoff enqueues into
    // the native session in this order, so it also downloads in this order.
    return orderByWatchPriority(queued, this.store.getState().playlist);
  }

  private async wipeRound(roundId: string): Promise<void> {
    const startedAt = Date.now();
    this.store.setWiping(true);
    // Invalidate any in-flight handoff for THIS round (other rounds'
    // epochs are untouched, so their in-flight downloads keep running).
    this.wipeEpochByRound.set(roundId, this.wipeEpoch(roundId) + 1);

    try {
      const items = Object.values(this.store.getState().items).filter(
        (item) => item.roundId === roundId,
      );

      const paths: string[] = [];
      const idByPath = new Map<string, string>();
      const failedPaths: string[] = [];
      const locked: MobileVideo[] = [];

      for (const item of items) {
        // Cancel only THIS round's pending retry timers — other rounds keep
        // their backoff timers so their failed items still recover. (Clearing
        // all timers here would strand other rounds' failed items, since
        // tick() only ever starts "queued" items.)
        this.clearRetryTimer(item.id);

        if (!item.download) {
          continue;
        }

        locked.push(item);

        // Keep the owner of every path: a file that does not delete must keep
        // its id marked as having residue, or the next download of that video
        // skips its pre-delete and collides with the leftover.
        const own = [
          item.download.filePath ?? item.id,
          ...(item.download.posterFilePath
            ? [item.download.posterFilePath]
            : []),
        ];

        for (const path of own) {
          paths.push(path);
          idByPath.set(path, item.id);
        }
      }

      // Clearing live progress is in-memory; one notification for the round
      // rather than one per video.
      downloadProgressStore.reset(
        locked.map((item) => item.id),
        { remove: true },
      );

      // Only ids that can still hold a native task need cancelling: a
      // completed or failed download's task is already terminal, and a queued
      // one was never handed off.
      //
      // That filter is NOT selective when it matters, though. The engine hands
      // a round's whole queue to the session up front, and the ones waiting
      // for a transfer slot are suspended natively while still recorded
      // "running" — so switching the toggle off mid-drain leaves `live` as
      // essentially the entire round. Cancelling those one at a time is the
      // same per-video bridge storm the batched delete below exists to avoid,
      // moved one step earlier. Hence one batched cancel, with the per-item
      // loop kept only for a plugin that lacks the batch method.
      const live = locked.filter(
        (item) =>
          item.download?.status === "running" ||
          item.download?.status === "paused",
      );

      if (live.length > 0 && (await stopBatch(live.map((i) => i.id))) == null) {
        for (const item of live) {
          try {
            await CapacitorDownloader.stop({ id: item.id });
          } catch {
            // Plugin may not know the task — ignore.
          }
        }
      }

      // Hold EVERY affected id's file lock across the batched delete, for the
      // same reason the per-item version held one: a completion event still
      // verifying when the wipe lands must drop rather than bless bytes this
      // is about to remove. Acquiring them in sorted order keeps a consistent
      // global order, so this can't deadlock against another multi-lock
      // caller.
      await this.withFileLocks(
        locked.map((item) => item.id),
        async () => {
          const result = await deleteFilesBatch(paths);

          if (result == null) {
            // No batch method (patch dropped, or the web stub) — fall back to
            // one call per file, still recording what survived.
            for (const path of paths) {
              if (!(await tryDeleteFile(path))) {
                failedPaths.push(path);
              }
            }
          } else {
            failedPaths.push(...result.failed);
          }

          const stillHasAFile = new Set(
            failedPaths
              .map((path) => idByPath.get(path))
              .filter((id): id is string => id != null),
          );

          for (const item of locked) {
            this.bumpAttemptEpoch(item.id);

            // Task stopped and file gone: nothing native is left under this
            // id, so a later download starts clean and skips the cleanup.
            //
            // A survivor is ADDED, not merely left alone: the id may never
            // have been marked in the first place (nothing marks a download
            // this process did not hand off — a record restored by a hydrate
            // after start, say), and "retain what is there" would then retain
            // nothing. The next download of that video must clear the
            // destination, or the plugin's move refuses to overwrite it and
            // the download fails into a retry.
            if (stillHasAFile.has(item.id)) {
              this.mayHaveNativeResidue.add(item.id);
            } else {
              this.mayHaveNativeResidue.delete(item.id);
            }
          }
        },
      );

      this.store.clearDownloadsForRound(roundId);

      devDebugLog("downloads", "wipe finished", {
        roundId,
        videos: locked.length,
        files: paths.length,
        stopped: live.length,
        // Non-zero means files survived the wipe: those videos still occupy
        // space the funder asked to reclaim, and their ids stay marked.
        undeleted: failedPaths.length,
        ms: Date.now() - startedAt,
      });
    } finally {
      this.store.setWiping(false);
    }
  }

  // Run `fn` holding the file lock for every id. Sorted so every multi-lock
  // caller acquires in the same order.
  private withFileLocks<T>(ids: string[], fn: () => Promise<T>): Promise<T> {
    const ordered = [...new Set(ids)].sort();

    return ordered.reduceRight<() => Promise<T>>(
      (next, id) => () => this.withFileLock(id, next),
      fn,
    )();
  }

  // Going offline deliberately does NOT touch "running" items: their native
  // background URLSession tasks survive connectivity loss (background
  // sessions always wait for connectivity and resume on their own), so
  // pausing + re-queueing them on reconnect would restart every transfer
  // from zero against a still-live native task — duplicate downloads racing
  // to move files onto the same path. Only items that actually FAILED while
  // offline sit in "paused" (see handleFailed/handleStartFailure), and
  // those are what reconnect re-queues.
  private handleOnlineChange(online: boolean): void {
    this.isOnline = online;

    if (!online) {
      return;
    }

    for (const item of this.store.itemsInOrder()) {
      if (item.download?.status === "paused") {
        this.store.updateDownload(item.id, {
          status: "queued",
          lastError: null,
        });
      }
    }

    this.tick();

    // A session that started offline couldn't cache transcripts/chapters/
    // posters for already-completed downloads; retry now that we're back
    // online. All three backfills are idempotent — they skip items that are
    // already cached — so this is safe to run on every reconnect.
    this.runBackfills("reconnect", {
      transcripts: true,
      chapters: true,
      posters: true,
    });
  }

  // True when the on-disk file's size matches what THIS download was
  // expected to fetch — the integrity check that catches saved error bodies
  // and truncated transfers. The expected size comes from the download
  // record's served-file stamp (the signed URL serves the 720p rendition
  // when one exists, whose size differs from the catalog's original-upload
  // sizeBytes); legacy unstamped records fall back to the catalog size. An
  // unverifiable download is treated as matching, or it would fail forever —
  // each completed download deleted and retried, and launch reconcile
  // dropping good offline copies. See downloadVerification.ts.
  private downloadSizeMatches(item: MobileVideo, stat: LocalFileStat): boolean {
    return downloadSizeMatches(item, stat.size);
  }

  // Whether AVFoundation can open the file the size check already accepted.
  // A stat without a URI (the web filesystem) has nothing to hand native, so
  // it gets no verdict, like any other unverifiable file.
  private verifyMedia(
    item: MobileVideo,
    stat: LocalFileStat,
  ): Promise<MediaVerification | null> {
    const mimeType = resolveLocalFileMimeType(item);

    // Downloaded files have no extension, so without a type hint AVFoundation
    // can't recognise even a perfect file — that is "unknown", not "corrupt",
    // and must not delete and block a good copy.
    if (stat.uri === undefined || mimeType === null) {
      return Promise.resolve(null);
    }

    return verifyDownloadedMedia({ uri: stat.uri, mimeType });
  }

  // Play-time self-heal: the native player refused to play a downloaded file
  // (corrupt bytes that slipped past the size check, or a download recorded
  // before verification shipped). Delete the broken file and reset the record
  // to queued so the engine re-downloads it with a freshly signed URL; the
  // player streams for the rest of its session meanwhile.
  async reportBrokenDownload(videoId: string, reason: string): Promise<void> {
    // Under the file lock (status re-checked inside): the heal's delete must
    // not interleave with a completion event's stat→record verification.
    await this.withFileLock(videoId, async () => {
      const item = this.store.getState().items[videoId];

      if (item?.download?.status !== "completed") {
        return;
      }

      await safeDeleteFile(item.download.filePath ?? videoId);
      this.bumpAttemptEpoch(videoId);

      this.store.updateDownload(videoId, {
        status: "queued",
        startedAtIso: null,
        completedAtIso: null,
        filePath: null,
        lastError: reason,
      });
    });

    this.tick();
  }

  // On launch (each item under its file lock — a rebound task can complete
  // mid-reconcile, and its verification must not interleave with the stats
  // and deletes below):
  //   - running/paused items may have actually finished while the app was
  //     terminated (file landed in <Documents>/<id> via the URLSession
  //     delegate but our listener never fired). Promote them silently.
  //   - running items whose rebound native task is still transferring keep
  //     draining: the patched plugin rebinds surviving URLSession tasks on
  //     relaunch, so re-queueing them here would stop each live task and
  //     restart its transfer from zero every time the app reopens mid-drain.
  //   - running items with no live task and no usable file are stale;
  //     re-queue so the engine restarts them.
  //   - completed items whose file vanished are silently dropped.
  //   - running/paused items whose file landed but AVFoundation can't open
  //     are blocked, not re-queued: the same file would fail the same way.
  //   - completed items never media-verified (recorded before playability
  //     verification shipped) get one AVFoundation check; the stamp it leaves
  //     keeps later launches from repeating it.
  private async reconcile(): Promise<string[]> {
    const startedAt = Date.now();
    let statted = 0;
    const legacyUnverified: string[] = [];

    for (const item of this.store.itemsInOrder()) {
      const status = item.download?.status;

      if (
        status === "running" ||
        status === "paused" ||
        status === "completed"
      ) {
        statted += 1;
      }

      if (status === "running" || status === "paused") {
        await this.withFileLock(item.id, async () => {
          const stat = await safeStatFile(item.id);
          const landed =
            stat.exists &&
            this.downloadSizeMatches(item, stat) &&
            !isDownloadGenerationStale(item);

          if (landed) {
            // Promoted unstamped: the playability check the completion event
            // would have run happens in the deferred pass (see
            // verifyLegacyDownloads) so a terminated background drain's many
            // landed files don't hold up this launch's first transfer.
            legacyUnverified.push(item.id);
            // The start-time generation stamp (when present) stands. An
            // unstamped (pre-upgrade) record keeps its start time as the
            // completion time instead of launch-time "now": the player's
            // staleness fallback compares this against the catalog
            // generation, and a launch-time value would make bytes from
            // before a media replacement look current. Start time errs
            // toward stale — a skipped report, never a wrong one. Any
            // verification stamp is an earlier attempt's and is cleared.
            this.store.updateDownload(item.id, {
              status: "completed",
              completedAtIso:
                item.download?.fileUploadedAt !== undefined
                  ? new Date().toISOString()
                  : (item.download?.startedAtIso ?? null),
              filePath: item.id,
              verifiedAtIso: undefined,
            });

            return;
          }

          // A file that exists but doesn't match the catalog size is a saved
          // server error body or a truncated transfer — never promote it;
          // delete it so nothing can play it (and so a live task's completion
          // move isn't blocked by a leftover at the destination).
          if (stat.exists) {
            await safeDeleteFile(item.id);
            this.bumpAttemptEpoch(item.id);
          }

          // A surviving background transfer keeps the item "running": the
          // rebound task's progress/completion/failure events drive the
          // record from here, exactly as if the app had never relaunched.
          if (status === "running" && (await this.hasLiveNativeTask(item.id))) {
            return;
          }

          this.store.updateDownload(item.id, {
            status: "queued",
            startedAtIso: null,
          });
        });
      } else if (status === "completed") {
        await this.withFileLock(item.id, async () => {
          const filePath = item.download?.filePath ?? item.id;
          const stat = await safeStatFile(filePath);

          if (!stat.exists) {
            downloadProgressStore.clear(item.id);
            this.store.remove(item.id);

            return;
          }

          if (await this.requeueIfBrokenCompleted(item, filePath, stat)) {
            return;
          }

          // Downloads recorded before playability verification shipped are
          // checked once — after this launch's transfers have started (see
          // verifyLegacyDownloads): on a large round the serial AVFoundation
          // loads would otherwise sit between boot and the first download.
          if (item.download?.verifiedAtIso === undefined) {
            legacyUnverified.push(item.id);
          }
        });
      } else if (
        status === "failed" &&
        item.download?.retryBlocked &&
        item.download.retryBlockedReason !== "unplayable"
      ) {
        // Storage-blocked downloads get one fresh attempt per launch: space
        // may have been freed since. Clear the block and re-queue; if the disk
        // is still full it re-blocks after one attempt (no tight loop).
        this.store.updateDownload(item.id, {
          status: "queued",
          retryBlocked: false,
          retryBlockedReason: undefined,
          lastError: null,
        });
      }
    }

    // Launch's other per-video loop, as a number. Each stat is a bridge round
    // trip and they run in series (the file locks make them), so on a large
    // downloaded round this is the startup cost sitting between the engine
    // booting and the first download moving — the same shape as the
    // navigation hold's old sweep, just once per launch instead of once per
    // route change. Read it before assuming a slow launch is something else.
    devDebugLog("downloads", "reconcile finished", {
      videos: this.store.itemsInOrder().length,
      statted,
      legacyUnverified: legacyUnverified.length,
      ms: Date.now() - startedAt,
    });

    return legacyUnverified;
  }

  // Playability check of downloads that have no verdict yet: records from
  // before verification shipped, and files that landed while the app was
  // terminated (promoted at launch without a completion event). Runs after this launch's transfers are moving: each check is a
  // serial AVFoundation load, and a funder's round (184 videos) would
  // otherwise delay the first download by the whole pass. Everything is
  // re-validated under the file lock — a record can be wiped, re-queued or
  // verified by a completion event while the pass is in flight.
  private async verifyLegacyDownloads(ids: string[]): Promise<void> {
    const startedAt = Date.now();
    let checked = 0;

    for (const id of ids) {
      await this.withFileLock(id, async () => {
        const item = this.store.getState().items[id];
        const download = item?.download;

        if (
          item === undefined ||
          download?.status !== "completed" ||
          download.verifiedAtIso !== undefined
        ) {
          return;
        }

        const filePath = download.filePath ?? id;
        const stat = await safeStatFile(filePath);

        if (!stat.exists) {
          return;
        }

        // Against the record as it is NOW, not as reconcile saw it: the
        // server catalog can refresh while this pass is in flight, and a
        // recording replaced since launch must be re-queued, not stamped as
        // verified (syncWithCatalog skips completed records, so the stale
        // bytes would otherwise sit until the next launch).
        if (await this.requeueIfBrokenCompleted(item, filePath, stat)) {
          return;
        }

        checked += 1;
        const media = await this.verifyMedia(item, stat);

        if (media?.playable === true) {
          this.store.updateDownload(id, {
            verifiedAtIso: new Date().toISOString(),
          });
        } else if (media?.playable === false) {
          await this.blockUnplayableFile(id, filePath, media.reason);
        }
      });

      await yieldToMainThread();
    }

    if (ids.length > 0) {
      devDebugLog("downloads", "legacy downloads verified", {
        candidates: ids.length,
        checked,
        ms: Date.now() - startedAt,
      });
    }
  }

  // Heals a completed download whose bytes no longer match what the catalog
  // describes: recorded before size verification shipped, or a recording
  // replaced server-side since (stale generation stamp — the per-download
  // size stamp always matches its own old file, so size alone can't catch a
  // replacement). Returns whether the record was re-queued. Caller holds the
  // file lock.
  private async requeueIfBrokenCompleted(
    item: MobileVideo,
    filePath: string,
    stat: LocalFileStat,
  ): Promise<boolean> {
    const generationStale = isDownloadGenerationStale(item);

    if (!generationStale && this.downloadSizeMatches(item, stat)) {
      return false;
    }

    await this.requeueBrokenCompleted(
      item,
      filePath,
      generationStale
        ? "Downloaded file predates the current recording"
        : "Downloaded file did not match the expected size",
    );

    return true;
  }

  // Re-queue rather than store.remove(): remove() also drops the video from
  // the playlist/bookmarks and loses watch progress, which the user shouldn't
  // pay for a broken file.
  private async requeueBrokenCompleted(
    item: MobileVideo,
    filePath: string,
    lastError: string,
  ): Promise<void> {
    await safeDeleteFile(filePath);
    this.bumpAttemptEpoch(item.id);
    this.store.updateDownload(item.id, {
      status: "queued",
      startedAtIso: null,
      completedAtIso: null,
      filePath: null,
      lastError,
    });
  }

  private async blockUnplayableFile(
    id: string,
    filePath: string,
    reason: string,
  ) {
    await safeDeleteFile(filePath);
    this.bumpAttemptEpoch(id);
    this.failWithoutRetry(id, "unplayable", unplayableMessage(reason));
  }

  // Handles are collected as they are acquired, and a failure part-way
  // releases the ones this attempt already holds. Recording them only after
  // ALL THREE succeed makes an earlier handle unreachable when a later call
  // rejects: `stop()` can only remove what `this.listeners` names, so the
  // stray callback outlives the engine — and because a failed start now
  // clears its memo to allow a retry, the next mount stacks a second copy on
  // top. Duplicated download events double-run completion verification,
  // media-cache writes and attempt counting.
  private async attachListeners(): Promise<void> {
    const acquired: PluginListenerHandle[] = [];

    try {
      acquired.push(
        await CapacitorDownloader.addListener("downloadProgress", (event) => {
          this.queueProgressUpdate(event.id, event.progress);
        }),
      );

      acquired.push(
        await CapacitorDownloader.addListener("downloadCompleted", (event) => {
          void this.handleCompleted(event.id);
        }),
      );

      acquired.push(
        await CapacitorDownloader.addListener("downloadFailed", (event) => {
          this.handleFailed(event.id, event.error);
        }),
      );
    } catch (error) {
      await this.removeListenerHandles(acquired);

      throw error;
    }

    this.listeners = acquired;
  }

  // Best-effort release — a handle that refuses to detach must not stop the
  // rest from being released (shared by teardown and the partial-attach
  // cleanup above).
  private async removeListenerHandles(
    handles: readonly PluginListenerHandle[],
  ): Promise<void> {
    for (const handle of handles) {
      try {
        await handle.remove();
      } catch {
        // Ignore listener cleanup failures.
      }
    }
  }

  private queueProgressUpdate(id: string, progress: number): void {
    const item = this.store.getState().items[id];

    if (item?.download?.status !== "running") {
      return;
    }

    // A progress event proves this task is actually transferring. If it was
    // recorded as waiting for a native transfer slot, the plugin has since
    // promoted it (promotion is native-side and has no event of its own) —
    // flip the flag so the queue UI moves it from "Queued" to "Downloading".
    if (item.download.awaitingNativeSlot === true) {
      this.store.updateDownload(id, { awaitingNativeSlot: false });
    }

    if (this.progressFlushTimer) {
      this.pendingProgress.set(id, progress);
      return;
    }

    const now = Date.now();
    const elapsed = now - this.lastProgressFlushAt;

    if (elapsed < progressFlushIntervalMs) {
      this.pendingProgress.set(id, progress);
      this.progressFlushTimer = setTimeout(() => {
        this.progressFlushTimer = null;
        this.flushPendingProgress();
      }, progressFlushIntervalMs - elapsed);
      return;
    }

    this.lastProgressFlushAt = now;
    downloadProgressStore.set(id, progress);
  }

  private flushPendingProgress(): void {
    if (this.pendingProgress.size === 0) {
      return;
    }

    const state = this.store.getState();

    for (const [id, progress] of this.pendingProgress) {
      const item = state.items[id];

      if (item?.download?.status === "running") {
        downloadProgressStore.set(id, progress);
      }
    }

    this.lastProgressFlushAt = Date.now();
    this.pendingProgress.clear();
  }

  private async handleCompleted(id: string): Promise<void> {
    // Snapshot before any await: if a superseding attempt (or reconcile/the
    // play-time heal) clears this id's destination while the event waits on
    // the file lock, the epoch bump marks these bytes as gone.
    const epoch = this.attemptEpoch(id);

    await this.withFileLock(id, () => this.verifyCompleted(id, epoch));
  }

  private async verifyCompleted(id: string, epoch: number): Promise<void> {
    // The plugin fires `downloadCompleted` as soon as its task finishes, but a
    // truncated transfer — or an error/HTML body saved in place of the video —
    // still lands here. Trusting it blindly records a "completed" download
    // whose file is missing or zero-byte: the download indicator counts it as
    // present and the player hands AVPlayer a file:// URL to nothing, so the
    // native controls appear but tapping play does nothing. Confirm real bytes
    // landed first; otherwise route through the failure path so the engine
    // retries (online) or pauses (offline) like any other failed download.
    const stat = await safeStatFile(id);
    const item = this.store.getState().items[id];

    // A newer attempt owns this destination now — while this event waited on
    // the file lock, the pre-download cleanup deleted the file it describes
    // (and may already be downloading a replacement). Judging by these bytes
    // would bless deleted ones as "completed" or park the live attempt in
    // "failed"; the new task reports its own events.
    if (this.attemptEpoch(id) !== epoch) {
      return;
    }

    if (!stat.exists || !stat.size) {
      this.handleFailed(id, "Downloaded file was missing or empty");
      return;
    }

    // The patched downloader rejects non-2xx responses and short bodies
    // natively, so a GCS XML error body (expired signed URL, missing object)
    // should no longer land here. The size check stays as defense in depth:
    // an unpatched plugin (a version bump that dropped the patch) saves
    // whatever the server responds with, and the served file's size can
    // drift from the size stamped when its URL was signed. Any mismatch means
    // the bytes aren't the video: delete the impostor so nothing can play or
    // resurrect it, and route through the failure path for a retry with a
    // freshly signed URL.
    if (item && !this.downloadSizeMatches(item, stat)) {
      await safeDeleteFile(id);
      this.handleFailed(
        id,
        `Downloaded file size ${stat.size} does not match expected ${expectedDownloadSizeBytes(item)}`,
      );
      return;
    }

    // The recording can be replaced while this task is in flight: the
    // catalog row now carries the new generation but these bytes (and their
    // size stamp, which they match) describe the old file. Reject them so
    // the failure path retries with a freshly signed URL of the new
    // generation, instead of recording stale bytes as completed until the
    // next launch reconcile.
    if (item && isDownloadGenerationStale(item)) {
      await safeDeleteFile(id);
      this.handleFailed(id, "Recording was replaced while downloading");
      return;
    }

    // Right size, right generation — but a corrupt or non-video file of the
    // right length still passes both, and only AVFoundation can tell. No
    // verdict (an app build predating verifyMedia, a failed bridge call)
    // completes unstamped rather than failing a download that may be fine.
    // A "no" is final rather than a retry: the same URL serves the same
    // bytes, so retrying would re-download the whole file every backoff.
    const media = item ? await this.verifyMedia(item, stat) : null;

    if (media?.playable === false) {
      await this.blockUnplayableFile(id, id, media.reason);
      this.tick();
      return;
    }

    // The generation stamp set at start time stands — re-reading the item
    // here could label old bytes with a generation attached mid-download.
    // The verification stamp is always rewritten: a re-download's bytes are
    // not the ones an earlier attempt verified.
    this.store.updateDownload(id, {
      status: "completed",
      completedAtIso: new Date().toISOString(),
      filePath: id,
      lastError: null,
      verifiedAtIso:
        media?.playable === true ? new Date().toISOString() : undefined,
    });

    this.clearRetryTimer(id);
    this.cacheTranscriptIfNeeded(id);
    this.cacheChaptersIfNeeded(id);
    this.cachePosterIfNeeded(id);
    this.tick();
  }

  // Why a sweep ran. Reported with its size (see runBackfills) because a
  // sweep's COST is now bounded but its FREQUENCY still isn't understood: the
  // device log shows one firing ~150ms after a navigation hold, and a
  // navigation has no business re-walking the round. Whoever picks that up
  // needs to know which trigger fired, which the old silent sweeps couldn't
  // say.
  private static readonly backfillReasons = [
    "startup",
    "reconnect",
    "catalog-sync",
  ] as const;

  // Queue every video still missing offline content. Safe to call repeatedly
  // and from any trigger: the queues drop ids already queued or in flight, and
  // each task re-checks the store when it runs.
  private runBackfills(
    reason: (typeof DownloadEngine.backfillReasons)[number],
    kinds: { transcripts: boolean; chapters: boolean; posters: boolean },
  ): void {
    let transcripts = 0;
    let chapters = 0;
    let posters = 0;

    for (const item of this.store.itemsInOrder()) {
      if (item.download?.status !== "completed") {
        continue;
      }

      // `chapters == null` means "not fetched yet" (an empty array means
      // "fetched, none exist"), so it's the signal that work remains.
      if (kinds.transcripts && item.transcriptId && !item.transcriptContent) {
        this.cacheTranscriptIfNeeded(item.id);
        transcripts += 1;
      }

      if (kinds.chapters && item.transcriptId && item.chapters == null) {
        this.cacheChaptersIfNeeded(item.id);
        chapters += 1;
      }

      if (
        kinds.posters &&
        shouldCachePoster(item.download, item.activePosterId)
      ) {
        this.cachePosterIfNeeded(item.id);
        posters += 1;
      }
    }

    if (transcripts + chapters + posters === 0) {
      return;
    }

    // The number that was missing when this shape of work was invisible: how
    // much a single sweep asks for, and therefore what it would have put on
    // the wire at once before the queues bounded it.
    devDebugLog("downloads", "backfill queued", {
      reason,
      transcripts,
      chapters,
      posters,
    });
  }

  // Fetches the transcript content for a downloaded video and stashes it in
  // the store so it's available offline. Best-effort: a network error here
  // doesn't fail the download; the next opportunity (engine restart with the
  // toggle on, or the user opening the video online) will retry.
  // Runs one offline-caching request under a deadline, so a stalled network
  // call releases its queue slot (see mediaCacheRequestTimeoutMs). The signal
  // is passed INTO the request so the transfer is actually abandoned, not
  // merely stopped being awaited.
  private async withCacheRequestTimeout<T>(
    run: (signal: AbortSignal) => Promise<T>,
  ): Promise<T> {
    const abort = new AbortController();
    const timer = setTimeout(() => {
      abort.abort(
        new Error(
          `Media cache request timed out after ${Math.round(mediaCacheRequestTimeoutMs / 1000)}s`,
        ),
      );
    }, mediaCacheRequestTimeoutMs);

    try {
      return await run(abort.signal);
    } finally {
      clearTimeout(timer);
    }
  }

  private cacheTranscriptIfNeeded(id: string): void {
    this.transcriptCacheQueue.run(id, () => this.runCacheTranscript(id));
  }

  private async runCacheTranscript(id: string): Promise<void> {
    // Re-read when the task RUNS, not when it was queued: by now the download
    // may have been wiped, or another caller's fetch may have filled this in.
    const item = this.store.getState().items[id];

    // A wipe keeps the row and only nulls its download and media, so
    // "transcript missing" is TRUE for a video the funder just deleted — this
    // would refetch it and write a fresh side record for a round whose
    // storage they asked to reclaim. Gate on the download the cache belongs
    // to, which the poster path always did and these two never did.
    if (!item?.transcriptId || item.download?.status !== "completed") {
      return;
    }

    if (item.transcriptContent) {
      return;
    }

    try {
      const result = await this.withCacheRequestTimeout((signal) =>
        trpcVanillaDevice.getTranscriptContent.query(
          { roundId: item.roundId, transcriptId: item.transcriptId as string },
          { signal },
        ),
      );

      if (result.content != null) {
        this.store.setTranscriptContent(id, result.content);
      }
    } catch {
      // Ignore — caching transcripts is best-effort.
    }
  }

  // Fetches a downloaded video's chapters and stashes them for offline use.
  // Stores an empty array when none exist so the backfill won't keep retrying.
  // Best-effort: a network error leaves `chapters` null to retry next time.
  private cacheChaptersIfNeeded(id: string): void {
    this.chapterCacheQueue.run(id, () => this.runCacheChapters(id));
  }

  private async runCacheChapters(id: string): Promise<void> {
    // Re-read at RUN time, and gate on the download, both as in
    // runCacheTranscript (see the wipe note there).
    const item = this.store.getState().items[id];

    if (!item?.transcriptId || item.download?.status !== "completed") {
      return;
    }

    if (item.chapters != null) {
      return;
    }

    try {
      const result = await this.withCacheRequestTimeout((signal) =>
        trpcVanillaDevice.getTranscriptChapters.query(
          { roundId: item.roundId, transcriptId: item.transcriptId as string },
          { signal },
        ),
      );

      this.store.setChapters(id, result.chapters ?? []);
    } catch {
      // Ignore — caching chapters is best-effort.
    }
  }

  // Public entry point: ask the engine to (re)cache a video's poster, e.g. when
  // a thumbnail notices its local file is stale (the active poster changed).
  // Fire-and-forget and best-effort — no-ops when nothing needs caching.
  ensurePosterCached(videoId: string): void {
    this.cachePosterIfNeeded(videoId);
  }

  // Fetches the active poster image for a downloaded video and stores it on
  // disk so its thumbnail shows offline. Best-effort, like transcript caching:
  // a network error doesn't fail the download; engine restart retries.
  private cachePosterIfNeeded(id: string): void {
    this.posterCacheQueue.run(id, () => this.runCachePoster(id));
  }

  private async runCachePoster(id: string): Promise<void> {
    // The queue owns de-duplication now (it was this path's own in-flight set
    // before — the one the transcript and chapter paths never got).
    if (!this.isOnline) {
      return;
    }

    const item = this.store.getState().items[id];

    if (
      !item?.download ||
      !shouldCachePoster(item.download, item.activePosterId)
    ) {
      return;
    }

    // videoPosterUrl handles the base itself (absolute in bundled native
    // builds) — never prepend one.
    const posterUrl = videoPosterUrl(item.id, item.activePosterId);

    if (!posterUrl) {
      return;
    }

    try {
      // The WHOLE exchange runs under the deadline, not just its first await:
      // fetch() resolves when the headers arrive, so reading the body outside
      // this callback would leave a stalled body holding a queue slot forever
      // — the exact failure the deadline exists to prevent. (The two tRPC
      // calls above are already whole-exchange: they resolve after parsing.)
      const base64 = await this.withCacheRequestTimeout(async (signal) => {
        const response = await fetch(posterUrl, {
          headers: buildDeviceAuthHeaders(),
          signal,
        });

        if (!response.ok) {
          return null;
        }

        return blobToBase64(await response.blob());
      });

      if (base64 == null) {
        return;
      }

      const posterPath = `${id}.poster`;

      await Filesystem.writeFile({
        directory: Directory.Documents,
        path: posterPath,
        data: base64,
      });

      // The item may have been wiped while we were fetching; updateDownload
      // no-ops if the download record is gone.
      this.store.updateDownload(id, {
        posterFilePath: posterPath,
        cachedPosterId: item.activePosterId,
      });
    } catch {
      // Best-effort — ignore.
    }
  }

  private handleFailed(id: string, error: string): void {
    const item = this.store.getState().items[id];

    if (!item?.download) {
      return;
    }

    // A failure event can arrive for an already-completed download — e.g. an
    // orphaned duplicate task whose file move hit the verified file already
    // at the destination. The completed record describes verified bytes on
    // disk; resetting it to failed/queued would delete and re-download them.
    if (item.download.status === "completed") {
      return;
    }

    // A block for an unplayable file is final: a late event from a
    // superseded task must not turn it into a retry that downloads the same
    // file again. Only a different served file (syncWithCatalog) or the
    // round toggle lifts it.
    if (
      item.download.status === "failed" &&
      item.download.retryBlockedReason === "unplayable"
    ) {
      return;
    }

    if (!this.isOnline) {
      this.store.updateDownload(id, { status: "paused", lastError: error });
      return;
    }

    const attempts = item.download.attempts + 1;

    if (this.store.isRoundDownloadEnabled(item.roundId)) {
      if (isInsufficientStorageError(error)) {
        // Don't tight-retry against a full disk (there's no eviction to free
        // space); it stays failed until an explicit toggle off→on or the next
        // launch. Still tick so the rest of the queue keeps moving into the
        // freed slot.
        this.failWithoutRetry(id, "storage", insufficientStorageMessage);
        this.tick();
        return;
      }

      this.scheduleRetry(id, attempts, error);
      this.tick();
      return;
    }

    this.store.updateDownload(id, {
      status: "failed",
      lastError: error,
      attempts,
    });
    this.tick();
  }

  // Park a failure that retrying can't fix: failed, with no retry timer, and
  // blocked so syncWithCatalog won't re-enqueue it (it re-runs on every
  // catalog change). `reason` decides what lifts the block — see
  // retryBlockedReason on the persisted download.
  private failWithoutRetry(
    id: string,
    reason: RetryBlockedReason,
    lastError: string,
  ) {
    const download = this.store.getState().items[id]?.download;

    if (!download) {
      return;
    }

    this.clearRetryTimer(id);
    this.store.updateDownload(id, {
      status: "failed",
      lastError,
      attempts: download.attempts + 1,
      retryBlocked: true,
      retryBlockedReason: reason,
      filePath: null,
      completedAtIso: null,
    });
  }

  private scheduleRetry(id: string, attempts: number, error: string): void {
    // A retryable failure supersedes any earlier block, which would otherwise
    // outlive this attempt's backoff and strand the item after a relaunch.
    this.store.updateDownload(id, {
      status: "failed",
      lastError: error,
      attempts,
      retryBlocked: false,
      retryBlockedReason: undefined,
    });

    this.clearRetryTimer(id);

    const delay = Math.min(retryMaxMs, retryBaseMs * 2 ** (attempts - 1));

    const timer = setTimeout(() => {
      this.retryTimers.delete(id);

      const current = this.store.getState().items[id];

      if (!current?.download || current.download.status !== "failed") {
        return;
      }

      if (!this.store.isRoundDownloadEnabled(current.roundId)) {
        return;
      }

      this.store.updateDownload(id, { status: "queued" });
      this.tick();
    }, delay);

    this.retryTimers.set(id, timer);
  }

  private clearRetryTimer(id: string): void {
    const timer = this.retryTimers.get(id);

    if (timer) {
      clearTimeout(timer);
      this.retryTimers.delete(id);
    }
  }

  private clearAllRetryTimers(): void {
    for (const timer of this.retryTimers.values()) {
      clearTimeout(timer);
    }

    this.retryTimers.clear();
  }

  private wipeEpoch(roundId: string): number {
    return this.wipeEpochByRound.get(roundId) ?? 0;
  }

  private attemptEpoch(id: string): number {
    return this.attemptEpochById.get(id) ?? 0;
  }

  // Everything the store already knows about native residue at launch. A
  // record that never left "queued" cannot have either: the handoff stamps
  // "running" BEFORE it calls the native downloader, so an id still sitting at
  // "queued" was never handed off. Anything else — running, paused, failed,
  // completed, or carrying a file path or a spent attempt — may have left a
  // task or a file behind that this launch has to clear.
  private markResidueFromStore(): void {
    for (const item of this.store.itemsInOrder()) {
      const download = item.download;

      if (download == null) {
        continue;
      }

      if (
        download.status !== "queued" ||
        download.filePath != null ||
        download.attempts > 0
      ) {
        this.mayHaveNativeResidue.add(item.id);
      }
    }
  }

  private bumpAttemptEpoch(id: string): void {
    this.attemptEpochById.set(id, this.attemptEpoch(id) + 1);
  }

  // Queue `fn` behind any section already holding this id's file lock. The
  // stored chain never rejects (a failure must not wedge the lock), but the
  // caller still sees `fn`'s own rejection through the returned promise.
  private withFileLock<T>(id: string, fn: () => Promise<T>): Promise<T> {
    const previous = this.fileLockById.get(id) ?? Promise.resolve();
    const result = previous.then(fn);
    const settled = result.then(
      () => undefined,
      () => undefined,
    );

    this.fileLockById.set(id, settled);
    void settled.then(() => {
      if (this.fileLockById.get(id) === settled) {
        this.fileLockById.delete(id);
      }
    });

    return result;
  }

  // True while the plugin still has a live URLSession task bound to this id —
  // a background transfer that survived an app relaunch (the patched plugin
  // rebinds surviving tasks on launch, and task-addressing calls wait for
  // that rebind). DONE/ERROR tasks aren't live: their terminal event already
  // fired or is about to. A missing task rejects.
  private async hasLiveNativeTask(id: string): Promise<boolean> {
    try {
      const { state } = await CapacitorDownloader.checkStatus({ id });

      return state === "RUNNING" || state === "PENDING";
    } catch {
      return false;
    }
  }

  // True if this item's round was wiped (its epoch advanced) or its round's
  // toggle was turned off while a handoff await was in flight. Used to
  // bail before kicking off a real URLSession task that would otherwise leak
  // a file on disk for an item the store no longer tracks. Keyed per round so
  // wiping one round never strands another round's in-flight download.
  private isStaleRun(item: MobileVideo, token: number): boolean {
    return (
      token !== this.wipeEpoch(item.roundId) ||
      !this.store.isRoundDownloadEnabled(item.roundId)
    );
  }

  // A handoff must also abandon when anything moved the item out of the
  // "running" state it set synchronously at start — a native task's late
  // completion event recorded "completed" (proceeding would delete the file
  // that event just verified), a failure event parked it in "failed"/
  // "paused", or a re-queue put it back in "queued" (the next tick re-hands
  // it off cleanly once this handoff's slot frees).
  private isAbandonedRun(item: MobileVideo, token: number): boolean {
    return (
      this.isStaleRun(item, token) ||
      this.store.getState().items[item.id]?.download?.status !== "running"
    );
  }

  /**
   * Suspend in-flight download transfers for `navigationHoldMs` so a
   * navigation's API fetches aren't starved by them. Safe to call on every
   * route change: repeat calls extend the window, and everything here is
   * best-effort — a task that already finished (or was wiped) just no-ops.
   */
  holdForNavigation(windowMs: number = navigationHoldMs): void {
    if (!this.started) {
      return;
    }

    const alreadyHolding = this.navHoldTimer != null;
    this.navHoldUntil = Date.now() + windowMs;

    if (this.navHoldTimer) {
      clearTimeout(this.navHoldTimer);
    }

    this.navHoldTimer = setTimeout(
      () => this.releaseNavigationHold(),
      windowMs,
    );

    if (!alreadyHolding) {
      void this.suspendTransferringTasks();
    }
  }

  private navHoldActive(): boolean {
    return Date.now() < this.navHoldUntil;
  }

  // Suspend the transfers that are actually on the wire — the (at most five)
  // items holding a native slot, never the whole handed-off queue.
  //
  // "running" is not that set, and pausing it was wrong twice over. The engine
  // hands the entire round to iOS up front, so on the funder's 184-video round
  // this loop ran ~184 serial bridge round trips inside the navigation it was
  // supposed to be clearing the way for — the same per-video storm the batched
  // handoff removed (patches/README.md measured that shape at ~35s of
  // main-thread contention), except this one repeated on every route change.
  // Worse, the slot-less tasks are suspended by the plugin BECAUSE they have
  // no slot; `releaseNavigationHold` then resumed them, starting transfers the
  // concurrency cap had deliberately parked and leaving the plugin's slot
  // bookkeeping describing a session it no longer matched. The cap exists so a
  // round doesn't run 184 transfers at a trickle — which is also the state
  // that starves every API fetch the rest of the app makes.
  //
  // Pausing a slot holder does NOT promote a waiting task in its place
  // (promotion runs only from the plugin's terminal delegate callback), so
  // suspending just these really does free the link for the hold's window. A
  // transfer that COMPLETES mid-hold does promote its replacement, natively
  // and with no JS event to hook — so the hold is best-effort, as it already
  // was (the old sweep's paused-then-promoted tasks resumed transferring the
  // same way). The next navigation catches it.
  private async suspendTransferringTasks(): Promise<void> {
    const startedAt = Date.now();
    const transferring = this.store.transferringItems();

    for (const item of transferring) {
      try {
        await CapacitorDownloader.pause({ id: item.id });
        this.navHeldTaskIds.add(item.id);
      } catch {
        // The task finished, failed, or never reached the native layer —
        // nothing to suspend, and nothing to resume later.
      }
    }

    // The cost of a navigation while a round downloads, as a number: `slots`
    // is what this sweep is allowed to touch (the plugin's cap), and `ms` is
    // how long the bridge calls held the main thread during the navigation.
    devDebugLog("downloads", "navigation hold suspended transfers", {
      slots: transferring.length,
      suspended: this.navHeldTaskIds.size,
      ms: Date.now() - startedAt,
    });
  }

  private releaseNavigationHold(): void {
    if (this.navHoldTimer) {
      clearTimeout(this.navHoldTimer);
      this.navHoldTimer = null;
    }

    this.navHoldUntil = 0;

    // Only ids THIS engine suspended, which is only ever slot holders (see
    // suspendTransferringTasks) — resuming a task the plugin is holding back
    // for lack of a slot would run it outside the concurrency cap.
    const held = [...this.navHeldTaskIds];
    this.navHeldTaskIds.clear();

    for (const id of held) {
      // A resumed id that was wiped or completed mid-hold rejects; ignore.
      void CapacitorDownloader.resume({ id }).catch(() => {});
    }

    this.tick();
  }

  private tick(): void {
    // `anyRoundDownloadEnabled` short-circuits only when no round wants
    // downloads. During a `wipeRound`, other enabled rounds may still tick;
    // the wiped round's items are guarded instead by the per-round wipe epoch
    // in the handoff (`isStaleRun`), so a completed event mid-wipe can't
    // start a fresh download for the round being cleared.
    if (
      !this.started ||
      !this.isOnline ||
      // A navigation hold also pauses slot-filling: starting fresh tasks
      // mid-hold would put new transfers on the pipe the hold just cleared.
      this.navHoldActive() ||
      !this.store.anyRoundDownloadEnabled()
    ) {
      return;
    }

    // Hand EVERY queued item to the native downloader in one burst per
    // round: a single batched signed-URL request covers the queue (chunked
    // at signedUrlBatchSize), then each item's native task is kicked off
    // back-to-back — highest watch priority first, so the native session
    // enqueues (and therefore drains) in the order the funder is likely to
    // need. Per-item URL fetches (the previous design) needed a JS re-tick
    // between waves, which a suspended WebView can't run — locking the phone
    // seconds after enabling Download All stranded the tail of the queue
    // un-enqueued until the next foreground. The handoff synchronously
    // transitions its items to "running" before its first await, and the
    // pick skips ids still mid-handoff — an item re-queued while its handoff
    // is in flight must wait for it to settle, or two concurrent handoffs
    // would interleave their stamps and stack two native tasks on one id.
    const queued = this.queuedByWatchPriority(this.handoffInFlight);

    if (queued.length === 0) {
      return;
    }

    // Signed URLs are minted per round (the route authorizes one roundId).
    const byRound = new Map<string, MobileVideo[]>();

    for (const item of queued) {
      const bucket = byRound.get(item.roundId) ?? [];
      bucket.push(item);
      byRound.set(item.roundId, bucket);
    }

    for (const [roundId, roundItems] of byRound) {
      const token = this.wipeEpoch(roundId);

      for (let i = 0; i < roundItems.length; i += signedUrlBatchSize) {
        const chunk = roundItems.slice(i, i + signedUrlBatchSize);

        for (const item of chunk) {
          this.handoffInFlight.add(item.id);
        }

        void this.startDownloadBatch(roundId, chunk, token).finally(() => {
          for (const item of chunk) {
            this.handoffInFlight.delete(item.id);
          }

          this.tick();
        });
      }
    }
  }

  // Hands one round's queued chunk to the native downloader: ONE batched
  // signed-URL request, then each item's native kickoff in chunk order.
  // Failures are per item — a missing URL or a kickoff error routes that
  // item through the same pause/retry/storage rules as a native failure
  // event, while the rest of the chunk proceeds. Only the batch request
  // itself failing (network, auth) fails the whole chunk.
  private async startDownloadBatch(
    roundId: string,
    items: MobileVideo[],
    token: number,
  ): Promise<void> {
    // Reset the live progress fraction to 0 for this fresh attempt (a
    // re-download of a previously-completed item would otherwise show its old
    // fraction until the first tick). Progress lives off the durable store.
    downloadProgressStore.reset(items.map((item) => item.id));

    // One commit for the whole chunk, like the enqueue that fed it: this
    // marks up to `signedUrlBatchSize` items "running" in a single pass, and
    // per-item commits here re-serialized the blob and re-rendered the drawer
    // once per video in the same frozen window the toggle already caused.
    const startedAtIso = new Date().toISOString();
    this.store.updateDownloadMany(
      items.map((item) => ({
        id: item.id,
        patch: {
          status: "running" as const,
          startedAtIso,
          lastError: null,
          // Handed off but no native transfer slot granted yet — don't show a
          // re-download as "downloading" on a stale flag from its previous
          // attempt. kickoffDownload records the actual grant.
          awaitingNativeSlot: true,
          // Stamp the generation the fetch will serve NOW, not at completion —
          // a media replacement mid-download must not label old bytes with the
          // new generation. Unknown (pre-upgrade row) stays absent.
          ...(item.fileUploadedAt !== undefined
            ? { fileUploadedAt: item.fileUploadedAt }
            : {}),
        },
      })),
    );

    let responseByVideoId: Map<
      string,
      {
        signedDownloadUrl: string;
        fileUploadedAt?: string | null;
        sizeBytes?: number | null;
        contentType?: string | null;
      }
    >;

    // Bounded and abortable: nothing in this chunk can start until this one
    // request settles, so a request that never settles strands every video in
    // it — "running" with no native task, and permanently skipped by the pick
    // because `handoffInFlight` still lists them. A timeout turns that into an
    // ordinary per-item failure, which retries with backoff.
    const abort = new AbortController();
    const timeout = setTimeout(() => {
      abort.abort(
        new Error(
          `Timed out requesting download URLs after ${Math.round(signedUrlRequestTimeoutMs / 1000)}s`,
        ),
      );
    }, signedUrlRequestTimeoutMs);

    try {
      const { videos } = await trpcVanillaDevice.getVideoDownloadUrls.query(
        {
          roundId,
          videoIds: items.map((item) => item.id),
          // This build stamps and verifies the served file's size, so it
          // may receive renditions (see the route's opt-in rationale).
          preferRendition: true,
          // The whole queue is enqueued into the iOS background URLSession up
          // front and drains a few transfers at a time — possibly for hours,
          // with the app suspended. A task deep in the queue starts long
          // after its URL was signed, so the default 1h expiry would 403 the
          // tail of any large round.
          extendedExpiry: true,
        },
        { signal: abort.signal },
      );

      responseByVideoId = new Map(
        videos.map((video) => [video.videoId, video]),
      );
    } catch (error) {
      for (const item of items) {
        this.handleStartFailure(item, token, error);
      }

      return;
    } finally {
      clearTimeout(timeout);
    }

    // Split the chunk by whether it needs the careful path, KEEPING ITS ORDER.
    //
    // The per-item kickoff exists to serialize "cancel the old task, delete
    // the old file, start the new one" against a completion event for the SAME
    // id that may be mid-verification. That race needs an old task or an old
    // file to exist. For an id with neither — every video of a fresh install —
    // there is nothing to order against, so the file lock is guarding nothing
    // and consecutive ones collapse into a single batched native call.
    //
    // Order is not cosmetic here. `items` arrives sorted by watch priority,
    // and the native session grants five transfer slots and drains the rest
    // FIFO — so whatever reaches it first downloads first. Running every clean
    // item before every careful one would put a high-priority retry behind a
    // whole chunk of videos the funder is less likely to open next, which on a
    // large round is hours. So the chunk is walked in order and only
    // CONSECUTIVE clean items batch together. A fresh install is one run of
    // 184, exactly as before; a device with some residue pays a batch boundary
    // per careful item, which is still nothing like one call per video.
    const runs: (
      | { batched: MobileVideo[] }
      | { single: MobileVideo; response: DownloadUrlResponse }
    )[] = [];

    for (const item of items) {
      const response = responseByVideoId.get(item.id);

      if (!response) {
        // The route omits videos it can't serve (unknown id, no uploaded
        // file, a per-video GCS hiccup) — fail just this item into its
        // normal retry path.
        this.handleStartFailure(
          item,
          token,
          new Error("Video is not available for download"),
        );
        continue;
      }

      if (this.mayHaveNativeResidue.has(item.id)) {
        runs.push({ single: item, response });
        continue;
      }

      if (this.isAbandonedRun(item, token)) {
        continue;
      }

      const last = runs.at(-1);

      if (last != null && "batched" in last) {
        last.batched.push(item);
      } else {
        runs.push({ batched: [item] });
      }
    }

    const handoffStartedAt = Date.now();
    let batchedCount = 0;
    let singleCount = 0;

    for (const [index, run] of runs.entries()) {
      // Between runs, never before the first: the queue must still reach the
      // native session promptly, it just must not hold the thread while it
      // does.
      if (index > 0) {
        await yieldToMainThread();
      }

      if ("batched" in run) {
        batchedCount += run.batched.length;
        await this.kickoffCleanBatch(run.batched, responseByVideoId, token);
        continue;
      }

      singleCount += 1;

      try {
        await this.kickoffDownload(run.single, token, run.response);
      } catch (error) {
        this.handleStartFailure(run.single, token, error);
      }
    }

    // Both handoff paths, in one line, so the cost of enabling downloads is a
    // number we can read instead of something inferred from error spam.
    devDebugLog("downloads", "handoff finished", {
      roundId,
      batched: batchedCount,
      perItem: singleCount,
      // More than one batch means residue interleaved with clean videos and
      // split the runs — expected on a device that has downloaded before.
      runs: runs.length,
      ms: Date.now() - handoffStartedAt,
    });
  }

  // The batched handoff: every video that cannot have a native task or a file
  // of its own, started with ONE bridge call. This is the path a fresh
  // install's entire round takes.
  private async kickoffCleanBatch(
    items: MobileVideo[],
    responseByVideoId: Map<string, DownloadUrlResponse>,
    token: number,
  ): Promise<void> {
    if (items.length === 0) {
      return;
    }

    // Stamp what the URLs actually serve before handing off, in one commit.
    // Safe to do ahead of the call here (the per-item path deliberately stamps
    // inside its lock): with no old bytes under this id, there is no earlier
    // attempt whose file could be judged by this response.
    this.store.updateDownloadMany(
      items.map((item) => ({
        id: item.id,
        patch: stampsFromDownloadUrlResponse(
          responseByVideoId.get(item.id) ?? {},
        ),
      })),
    );

    // From here these ids may own a task and a destination file, so a later
    // attempt must clean up before starting. Marked BEFORE the call so an
    // attempt that dies mid-batch still leaves its successor knowing.
    for (const item of items) {
      this.mayHaveNativeResidue.add(item.id);
    }

    let results: BatchDownloadResult[] | null;

    try {
      results = await downloadBatch(
        items.map((item) => ({
          id: item.id,
          url: new URL(
            responseByVideoId.get(item.id)?.signedDownloadUrl ?? "",
            window.location.origin,
          ).toString(),
          destination: item.id,
          // User-initiated: the funder is waiting on these files for offline
          // review. Maps to URLSessionTask.highPriority in the patched plugin.
          priority: "high" as const,
        })),
      );
    } catch (error) {
      for (const item of items) {
        this.handleStartFailure(item, token, error);
      }

      return;
    }

    if (results == null) {
      // The plugin has no batch method (patch dropped by a version bump, or
      // the web stub). Support cannot be probed by looking — a Capacitor
      // plugin proxy answers every property access with a callable — so this
      // is where we find out, and the whole chunk falls back rather than
      // failing. These ids are marked as possibly having residue by now, so
      // the per-item path does its cleanup: redundant for this one chunk, and
      // the bridge remembers so no later chunk repeats it.
      await this.kickoffEachInTurn(items, responseByVideoId, token);

      return;
    }

    const resultById = new Map(results.map((result) => [result.id, result]));
    const tookASlot: string[] = [];

    // UNDER EACH ID'S FILE LOCK, like the per-item path. The epoch bump is
    // only safe there: a completion event snapshots the epoch, awaits its file
    // stat, then re-checks — so a bump landing between those two steps
    // discards a terminal event belonging to this very attempt, leaving the
    // download "running" with no native task until the next launch reconciles
    // it. A task from this batch CAN finish that fast: the first items are
    // resumed natively while the rest are still being created.
    //
    // The locks are in-memory, so taking a chunk's worth costs nothing next to
    // the bridge call that just returned.
    await Promise.all(
      items.map((item) =>
        this.withFileLock(item.id, async () => {
          this.bumpAttemptEpoch(item.id);

          const result = resultById.get(item.id);

          if (result == null || result.error != null) {
            this.handleStartFailure(
              item,
              token,
              new Error(result?.error ?? "Download was not started"),
            );

            return;
          }

          // A WIPE that landed while the batch was in flight leaves a live
          // task writing a file the store has no record of. The per-item path
          // gated on this immediately before its native call; the batch can
          // only check after, so it cleans up instead.
          if (this.isStaleRun(item, token)) {
            void this.discardOrphanedHandoff(item.id);

            return;
          }

          // Any OTHER reason the run is no longer "running" — most likely this
          // task's own completion, already verified under this lock. Leave it
          // alone: discarding would delete bytes that were just verified, and
          // writing the slot flag would reopen a settled record.
          if (this.isAbandonedRun(item, token)) {
            return;
          }

          // The patched plugin caps concurrent transfers: RUNNING took a slot,
          // PENDING waits natively. Only RUNNING needs a write — see
          // kickoffDownload for why the PENDING case is deliberately skipped.
          if (result.state !== "PENDING") {
            tookASlot.push(item.id);
          }
        }),
      ),
    );

    this.store.updateDownloadMany(
      tookASlot.map((id) => ({ id, patch: { awaitingNativeSlot: false } })),
    );
  }

  // The per-item handoff, in turn, yielding between items. Used for ids that
  // may have native residue, and as the fallback when the plugin has no batch
  // method.
  private async kickoffEachInTurn(
    items: MobileVideo[],
    responseByVideoId: Map<string, DownloadUrlResponse>,
    token: number,
  ): Promise<void> {
    for (const [index, item] of items.entries()) {
      if (index > 0) {
        await yieldToMainThread();
      }

      try {
        await this.kickoffDownload(
          item,
          token,
          responseByVideoId.get(item.id) ?? {},
        );
      } catch (error) {
        this.handleStartFailure(item, token, error);
      }
    }
  }

  // Undo a handoff whose run was abandoned while the batch call was in flight:
  // stop the task and remove the file it may already have written, under the
  // id's lock like every other path that deletes a destination.
  private async discardOrphanedHandoff(id: string): Promise<void> {
    try {
      await CapacitorDownloader.stop({ id });
    } catch {
      // Already finished or never started — nothing to stop.
    }

    await this.withFileLock(id, async () => {
      await safeDeleteFile(id);
      this.bumpAttemptEpoch(id);
    });
  }

  private async kickoffDownload(
    item: MobileVideo,
    token: number,
    response: DownloadUrlResponse,
  ): Promise<void> {
    const { signedDownloadUrl, fileUploadedAt, sizeBytes, contentType } =
      response;

    if (this.isAbandonedRun(item, token)) {
      return;
    }

    if (signedDownloadUrl == null) {
      // Same outcome as a video the route omitted: this attempt can't start,
      // so send it down the ordinary retry path instead of building a URL from
      // nothing (which would resolve to the app's own origin and 404).
      throw new Error("Video is not available for download");
    }

    const url = new URL(signedDownloadUrl, window.location.origin).toString();

    // Whether this id can have a native task or a leftover file at all. Read
    // ONCE, before the marking below, so the cleanup decision describes the
    // state this attempt actually found.
    const mayHaveResidue = this.mayHaveNativeResidue.has(item.id);

    // From here on it can: the download below creates the task and the file.
    // Marked BEFORE the call, so an attempt that dies mid-kickoff still leaves
    // its successor knowing to clean up.
    this.mayHaveNativeResidue.add(item.id);

    // Kill any native task still bound to this id — one that survived a
    // relaunch (the patched plugin rebinds tasks on launch) or an earlier
    // attempt this run supersedes. Two live URLSession tasks on one id
    // would race to move their files onto the same destination. The
    // patched plugin suppresses the cancellation's failure event, so this
    // can't bounce our own freshly-"running" record.
    if (mayHaveResidue) {
      try {
        await CapacitorDownloader.stop({ id: item.id });
      } catch {
        // No such task — ignore.
      }
    }

    // The delete→download below runs under the id's file lock: an orphan
    // task's completion can land during the stop() await above and still be
    // mid-verification when stop() resolves — a bare status gate would see
    // "running", delete the file under the verification, and let it record
    // "completed" for bytes that are gone (which the next launch reconcile
    // turns into a full remove: playlist, bookmarks, watch progress).
    await this.withFileLock(item.id, async () => {
      // Re-check under the lock: an orphan task's completion processed
      // while we awaited stop() (or this lock) verified the file and
      // recorded "completed"; deleting here would strand that record
      // pointing at nothing.
      if (this.isAbandonedRun(item, token)) {
        return;
      }

      // Re-stamp with what the URL actually serves — the catalog row this
      // run started from may be stale (media replaced since the last sync),
      // and mislabeling the new bytes would suppress their heal. The size/
      // contentType stamps describe the served file (the 720p rendition
      // when one exists), which verification and local playback must use
      // instead of the catalog's original-upload values.
      // Inside the lock and after its gate: an orphan
      // task's completion that wins the lock must verify its bytes against
      // the stamps of the attempt that STARTED it — stamping this response
      // earlier would judge those old bytes by the new response (deleting
      // them on a size change, or blessing them under a generation they
      // don't have), and a superseded run's late response could mutate the
      // new run's record.
      // Every stamp mirrors THIS attempt's response exactly — see
      // stampsFromDownloadUrlResponse (CI-tested) for the semantics and
      // the retry-forever failure modes any coercion here reintroduces.
      this.store.updateDownload(
        item.id,
        stampsFromDownloadUrlResponse({
          fileUploadedAt,
          sizeBytes,
          contentType,
        }),
      );

      try {
        // Clear any stale leftover from a prior attempt — when there can be
        // one. An id with no prior attempt on this device has no file to
        // clear, and asking anyway is a bridge round trip that always answers
        // "does not exist". A leftover this engine never knew about (a lost
        // or reset store) is handled natively: the patched plugin replaces a
        // file already at the destination when the download lands (see
        // patches/README.md), so a stock plugin's "same name already exists"
        // failure can't cost a full transfer here.
        if (mayHaveResidue) {
          await safeDeleteFile(item.id);
        }

        // Last gate before kicking off a real URLSession task — a wipe
        // between the safeDeleteFile above and this line would otherwise
        // leak the downloaded file on disk even though the store has no
        // record of it.
        if (this.isAbandonedRun(item, token)) {
          return;
        }

        const task = await CapacitorDownloader.download({
          id: item.id,
          url,
          destination: item.id,
          // User-initiated: the funder is waiting on these files for
          // offline review. Maps to URLSessionTask.highPriority in the
          // (patched — see patches/README.md) iOS plugin.
          priority: "high",
        });

        // The patched plugin caps concurrent transfers: RUNNING means this
        // task took a slot and is transferring; PENDING means it waits
        // natively for one. The handoff already stamped awaitingNativeSlot
        // true, so only the RUNNING case needs a write — flipping to false
        // here so the queue UI shows the item as downloading. Skipping the
        // PENDING write matters twice over: it avoids a no-op store persist
        // per queued item (the whole tail of a large round, serialized to
        // localStorage while racing the lock screen), and it can't clobber
        // a concurrent first-progress-event clear back to true when the
        // task was promoted natively between download() resolving and this
        // line. Promotion has no JS event — that first progress event is
        // what clears the flag for PENDING items.
        if (task.state !== "PENDING") {
          this.store.updateDownload(item.id, { awaitingNativeSlot: false });
        }
      } finally {
        // This attempt owns the destination now: an older task's event
        // still queued on the file lock must drop rather than judge the
        // new attempt by the old task's (deleted) bytes.
        this.bumpAttemptEpoch(item.id);
      }
    });
  }

  // The per-item failure path for a handoff that couldn't reach the native
  // downloader (the batch URL request failed, this item's URL was missing,
  // or its stop/delete/download kickoff threw). Mirrors handleFailed's
  // pause/retry/storage rules; tick() runs via the batch's `.finally()`.
  private handleStartFailure(
    item: MobileVideo,
    token: number,
    error: unknown,
  ): void {
    if (token !== this.wipeEpoch(item.roundId)) {
      return;
    }

    const message = error instanceof Error ? error.message : String(error);
    const current = this.store.getState().items[item.id];

    if (!current?.download) {
      return;
    }

    // Same superseded-handoff rule as the kickoff's isAbandonedRun gates:
    // if something moved the item out of "running" while this attempt was
    // in flight — an orphan task's completion recorded "completed", a
    // failure event parked it, a re-queue reset it — this attempt's error
    // is stale. Overwriting a completed record with "failed" here would
    // send the retry to delete verified bytes.
    if (current.download.status !== "running") {
      return;
    }

    if (!this.isOnline) {
      this.store.updateDownload(item.id, {
        status: "paused",
        lastError: message,
      });

      return;
    }

    const attempts = current.download.attempts + 1;

    if (this.store.isRoundDownloadEnabled(item.roundId)) {
      if (isInsufficientStorageError(message)) {
        // See handleFailed: don't tight-retry a full disk.
        this.failWithoutRetry(item.id, "storage", insufficientStorageMessage);
        return;
      }

      this.scheduleRetry(item.id, attempts, message);
      return;
    }

    this.store.updateDownload(item.id, {
      status: "failed",
      lastError: message,
      attempts,
    });
  }
}

export const mobileVideoStore = new MobileVideoStore({ persist: true });

let engineSingleton: DownloadEngine | null = null;

export function getDownloadEngine(): DownloadEngine {
  if (!engineSingleton) {
    engineSingleton = new DownloadEngine(mobileVideoStore);
  }

  return engineSingleton;
}
